import {useState, useCallback, useMemo} from 'react'
import {graphql} from 'relay-runtime'
import {useFragment} from 'react-relay'
import type {FieldEditorsFieldSingleSelectValue$key} from './__generated__/FieldEditorsFieldSingleSelectValue.graphql'
import type {FieldEditorsFieldTextValue$key} from './__generated__/FieldEditorsFieldTextValue.graphql'
import {Box, TextInput} from '@primer/react'
import type {IssueFieldSingleSelectOption} from '@github-ui/item-picker/IssueSingleSelectFieldPicker'
import {IssueSingleSelectFieldPicker} from '@github-ui/item-picker/IssueSingleSelectFieldPicker'
import {SectionHeader} from '../SectionHeader'
import {IssueFieldSingleSelectValueToken} from './IssueFieldSingleSelectValueToken'
import {Section} from '../Section'

type IssueFieldCommentEditorProps = {issueId: string; hideDivider: boolean}

type IssueFieldTextEditorProps = IssueFieldCommentEditorProps & {field: FieldEditorsFieldTextValue$key}

export const IssueFieldTextEditor = ({hideDivider, field}: IssueFieldTextEditorProps) => {
  const data = useFragment(
    graphql`
      fragment FieldEditorsFieldTextValue on IssueFieldTextValue {
        id
        field {
          ... on IssueFieldText {
            id
            name
          }
        }
        value
      }
    `,
    field,
  )
  const [text, setText] = useState(data.value || '')

  // const environment = useRelayEnvironment()
  const onBlur = () => {
    if (data.field.id) {
      // TODO issue_fields
      // commitSetIssueFieldValueMutation({
      //   environment,
      //   input: {issueId, issueFieldId: data.field.id, newValue: text},
      // })
    }
  }

  if (!data.field.name) return null

  return (
    <Section sectionHeader={<SectionHeader title={data.field.name} readonly />} hideDivider={hideDivider}>
      <TextInput
        aria-label={`The value for the ${data.field.name} field`}
        value={text}
        onChange={e => setText(e.target.value)}
        onBlur={onBlur}
        sx={{ml: 2, width: '100%'}}
      />
    </Section>
  )
}

type IssueFieldSingleSelectEditorProps = IssueFieldCommentEditorProps & {field: FieldEditorsFieldSingleSelectValue$key}

export const IssueFieldSingleSelectEditor = ({hideDivider, field}: IssueFieldSingleSelectEditorProps) => {
  const data = useFragment(
    graphql`
      fragment FieldEditorsFieldSingleSelectValue on IssueFieldSingleSelectValue {
        field {
          ... on IssueFieldSingleSelect {
            id
            name
          }
        }
        name
        description
        color
      }
    `,
    field,
  )

  // const environment = useRelayEnvironment()
  const onSelectionChanged = useCallback(
    // eslint-disable-next-line unused-imports/no-unused-vars
    (selectedOption: IssueFieldSingleSelectOption | null) => {
      if (data.field.id) {
        // TODO issue_fields
        // commitSetIssueFieldValueMutation({
        //   environment,
        //   input: {issueId, issueFieldId: data.field.id, newValue: selectedOption?.name || ''},
        // })
      }
    },
    [data.field.id],
  )

  const sectionHeader = useMemo(() => {
    return (
      <IssueSingleSelectFieldPicker
        fieldId={data.field.id || ''}
        selectedOption={data.name}
        onSelectionChange={onSelectionChanged}
        anchorElement={(anchorProps, ref) => (
          <SectionHeader title={data.field.name || ''} buttonProps={anchorProps} ref={ref} />
        )}
        readonly={false}
        isLazy
        shortcutEnabled
      />
    )
  }, [data.field.id, data.field.name, data.name, onSelectionChanged])

  if (!data.field.id || !data.field.name) return null

  return (
    <Section sectionHeader={sectionHeader} hideDivider={hideDivider}>
      <Box sx={{ml: 2, mt: 1}}>
        <IssueFieldSingleSelectValueToken
          name={data.name || ''}
          color={data.color}
          getTooltipText={isTextTruncated => (isTextTruncated ? data.description ?? '' : undefined)}
        />
      </Box>
    </Section>
  )
}

try{ IssueFieldTextEditor.displayName ||= 'IssueFieldTextEditor' } catch {}
try{ IssueFieldSingleSelectEditor.displayName ||= 'IssueFieldSingleSelectEditor' } catch {}